<template>
  <div class="flex flex-col w-full">

    <!-- Upload: ancho completo cuando no hay archivo -->
    <div v-if="!form.excelFile" class="bg-white border border-gray-200 rounded-lg p-4">
      <div class="flex gap-4 mb-3 items-end justify-end">
        <b-button type="is-primary"
          size="is-small"
          icon-pack="fas"
          icon-left="download"
          :loading="isCheckingTemplate"
          :disabled="isCheckingTemplate" @click="downloadTemplate">
          <span>Descargar plantilla</span>
        </b-button>
      </div>

      <b-field>
        <b-upload v-model="form.excelFile"
          drag-drop
          expanded
          @input="onFileSelected"
          accept=".xlsx,.xls,.csv">
          <section class="section">
            <div class="content has-text-centered">
              <p>
                <b-icon pack="fas" icon="upload" size="is-large" class="text-gray-400"></b-icon>
              </p>
              <p class="text-sm font-medium text-gray-700">Arrastra tu archivo Excel aquí o haz clic para seleccionar</p>
              <p class="text-xs text-gray-400 mt-1">Soporta .xlsx, .xls y .csv (Máx. 100MB)</p>
            </div>
          </section>
        </b-upload>
      </b-field>
    </div>

    <!-- Tabla + Panel de control: aparecen juntos al cargar archivo -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-start">

      <!-- Vista previa -->
      <div class="lg:col-span-2">
        <div class="border rounded-lg overflow-hidden" style="border-color: #ADC2EA;">
          <div class="panel-control__header">
            <h2 class="text-sm font-semibold text-white">Vista Previa (Primeras 20 Filas)</h2>
          </div>
          <div class="table-scroll">
            <table class="w-full border-collapse text-sm">
              <thead class="table-header">
                <tr>
                  <th
                    v-for="(column, index) in previewColumns"
                    :key="'column_' + index"
                    class="table-th"
                  >{{ column }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!previewRows.length">
                  <td :colspan="previewColumns.length" class="px-4 py-6 text-center text-xs" style="color: #7D99D4; border: 1px solid #ADC2EA;">
                    Procesando archivo...
                  </td>
                </tr>
                <tr
                  v-for="(row, idx) in previewRows"
                  :key="'row_' + idx"
                  class="table-row"
                  :class="idx % 2 === 0 ? 'table-row--even' : 'table-row--odd'"
                >
                  <td
                    v-for="(colName, colIndex) in previewColumns"
                    :key="'column_' + colIndex"
                    class="table-td"
                  >{{ row[colIndex] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Panel de control -->
      <div>
        <div ref="controlPanel" class="panel-control">
          <div class="panel-control__header">
            <h2 class="text-sm font-semibold text-white">Panel de Control</h2>
          </div>

          <div class="panel-control__body">
            <!-- Info archivo -->
            <div class="panel-info-group">
              <div class="panel-info-row">
                <span class="panel-info-label">Archivo</span>
                <span class="panel-info-value truncate" :title="form.excelFile.name">{{ form.excelFile.name }}</span>
              </div>
              <div class="panel-info-row">
                <span class="panel-info-label">Filas</span>
                <span class="panel-info-value">{{ totalRows.toLocaleString() }}</span>
              </div>
              <div class="panel-info-row">
                <span class="panel-info-label">Columnas</span>
                <span class="panel-info-value">{{ previewColumns.length }}</span>
              </div>
            </div>

            <!-- Estado -->
            <div class="mb-3">
              <label class="panel-info-label mb-1 block">Estado General</label>
              <p class="text-base font-bold" :class="statusClass">{{ statusText }}</p>
            </div>

            <!-- Progress bar -->
            <div class="mb-4">
              <div class="flex justify-between mb-2">
                <span class="panel-info-label">Progreso de Carga</span>
                <span class="text-xs font-bold" :class="progress === 100 ? 'text-green-600' : 'text-blue-600'">{{ progress }}%</span>
              </div>
              <div class="progress-track">
                <div
                  class="progress-bar"
                  :class="{ 'progress-bar--complete': progress === 100, 'progress-bar--active': isImporting && progress < 100 }"
                  :style="{ width: Math.max(progress, 0) + '%' }"
                ></div>
              </div>
              <p class="text-xs mt-2 text-center" style="color: #7D99D4;">
                {{ processedRows.toLocaleString() }} / {{ totalRows.toLocaleString() }} registros procesados
              </p>
            </div>

            <!-- Contadores -->
            <div class="grid grid-cols-3 gap-3 mb-4">
              <div class="counter-box counter-box--success p-3 rounded-lg text-center">
                <span class="block text-xs font-semibold uppercase mb-0.5" style="color: #4ade80;">Asignados</span>
                <span class="text-lg font-bold" style="color: #22c55e;">{{ createdRows.toLocaleString() }}</span>
              </div>
              <div class="counter-box counter-box--info p-3 rounded-lg text-center">
                <span class="block text-xs font-semibold uppercase mb-0.5" style="color: #3b82f6;">Actualizados</span>
                <span class="text-lg font-bold" style="color: #1d4ed8;">{{ updatedRows.toLocaleString() }}</span>
              </div>
              <div class="counter-box counter-box--failed p-3 rounded-lg text-center">
                <span class="block text-xs font-semibold uppercase mb-0.5" style="color: #d97706;">Fallidos</span>
                <span class="text-lg font-bold" style="color: #b45309;">{{ failedRows.toLocaleString() }}</span>
              </div>
            </div>

            <!-- Errores de validación de headers -->
            <div v-if="headerErrors.length" class="header-errors-block">
              <p class="header-errors-title">
                <b-icon pack="fas" icon="exclamation-triangle" size="is-small"></b-icon>
                Estructura del Excel incorrecta
              </p>
              <ul class="header-errors-list">
                <li v-for="(err, i) in headerErrors" :key="i">{{ err }}</li>
              </ul>
            </div>

            <!-- Validación OK -->
            <div v-else-if="form.excelFile && isHeaderValid" class="header-ok-block">
              <p class="header-ok-text">
                <b-icon pack="fas" icon="check-circle" size="is-small"></b-icon>
                Estructura del Excel correcta
              </p>
            </div>

            <!-- Antes de importar: Iniciar + Limpiar -->
            <div v-if="!isImporting && progress < 100" class="flex gap-2">
              <b-button
                @click="startImport"
                type="is-primary"
                size="is-small"
                class="flex-1"
                icon-pack="fas"
                icon-left="play"
                :disabled="!form.excelFile || !isHeaderValid"
              >
                Iniciar Importación
              </b-button>
              <b-button
                @click="resetImport"
                type="is-light"
                size="is-small"
                class="flex-1"
                icon-pack="fas"
                icon-left="times"
              >
                Limpiar
              </b-button>
            </div>

            <!-- Durante importación: solo loading -->
            <b-button
              v-if="isImporting"
              type="is-primary"
              size="is-small"
              class="w-full"
              loading
              disabled
            >
              Importando...
            </b-button>

            <!-- Finalizado: Aceptar -->
            <b-button
              v-if="!isImporting && progress === 100"
              @click="resetImport"
              type="is-success"
              size="is-small"
              class="w-full"
              icon-pack="fas"
              icon-left="check"
            >
              Aceptar
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Log en tiempo real -->
    <div v-if="form.excelFile" class="mt-4 border rounded-lg overflow-hidden" style="border-color: #ADC2EA;">
      <div class="panel-control__header flex justify-between items-center">
        <h2 class="text-sm font-semibold text-white">Log en Tiempo Real</h2>
        <b-button
          size="is-small"
          :type="failedRows > 0 ? 'is-warning' : 'is-light'"
          icon-pack="fas"
          icon-left="download"
          :disabled="allLogs.length === 0"
          @click="downloadReport"
        >
          Descargar Reporte (CSV)
        </b-button>
      </div>
      <div ref="logContainer" class="log-container">
        <p v-if="allLogs.length === 0" class="text-center py-4" style="color: #7D99D4;">Sin actividad registrada</p>
        <p
          v-for="(log, i) in allLogs"
          :key="i"
          class="log-entry"
          :class="{ 'log-entry--error': log.type === 'error' }"
        >
          <span class="log-time">[{{ log.time }}]</span> {{ log.message }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>

import Form from "vform";

import { importExtraAttributes, storeExtraAttributesDocument } from '../../services/layers.service';
import { uploadFile } from '@pictograms/shared/services/uppy-client.service';
import { IMPORT_EXTRA_ATTRS_COLUMNS, IMPORT_EXTRA_ATTRS_HEADER_ROWS } from './specs/importSpec';

// URL absoluta al bundle del Excel worker. Mismo patrón que CadWorkerClient:
// webpack.excel-worker.config.js (independiente) compila el worker como bundle
// self-contained a /public/workers/excel.worker.js. El componente lo carga
// vía URL absoluta usando ASSET_PATH (inyectado en build time por DefinePlugin).
//
//   - Local      : ASSET_PATH = '/'                       → '/workers/excel.worker.js'
//   - Producción : ASSET_PATH = 'https://cdn.cloudfront…' → URL absoluta CloudFront
//
// En producción cross-origin necesitamos el blob URL workaround porque los
// browsers rechazan `new Worker(crossOriginUrl)` incluso con CORS configurado.
const __EXCEL_WORKER_BASE = (typeof process !== 'undefined' && process.env && process.env.ASSET_PATH
  ? process.env.ASSET_PATH
  : '/').replace(/\/$/, '');
const EXCEL_WORKER_URL = `${__EXCEL_WORKER_BASE}/workers/excel.worker.js`;

function _isCrossOrigin(url) {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Crea un Worker desde una URL, manejando el caso cross-origin con fetch+blob.
 * Async: resuelve con el Worker creado o rechaza con error claro.
 */
async function createExcelWorker() {
  if (_isCrossOrigin(EXCEL_WORKER_URL)) {
    const resp = await fetch(EXCEL_WORKER_URL, { mode: 'cors', credentials: 'omit' });
    if (!resp.ok) {
      throw new Error(`Excel worker fetch falló: HTTP ${resp.status}`);
    }
    const text = await resp.text();
    if (text.trimStart().startsWith('<')) {
      throw new Error(`Excel worker URL devolvió HTML en lugar de JS: ${EXCEL_WORKER_URL}`);
    }
    const blob = new Blob([text], { type: 'application/javascript' });
    return new Worker(URL.createObjectURL(blob));
  }
  return new Worker(EXCEL_WORKER_URL);
}

export default {
  name: 'LayerImportExtraAttributes',

  props: {
    layer: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      form: new Form({
        excelFile: null,
      }),

      previewRows: [],
      previewColumns: [
        'ID',
        'Ejecutado',
        'CT',
        'Número de tracker',
        'Motor/No motor/Exterior',
      ],
      totalRows: 0,
      processedRows: 0,
      createdRows: 0,
      updatedRows: 0,
      failedRows: 0,
      isImporting: false,
      logs: [],
      errorLogs: [],

      excelWorker: null,
      isLoading: false,
      isReady: false,
      isProcessing: false,

      detectedHeaders: [],
      headerErrors: [],

      isCheckingTemplate: false,
    }
  },

  computed: {
    progress() {
      if (this.totalRows === 0) return 0;
      return Math.round((this.processedRows / this.totalRows) * 100);
    },
    statusText() {
      if (this.isImporting) return 'Importación en curso...';
      if (this.progress === 100) return 'Importación completada';
      return 'Importación lista para iniciar';
    },
    statusClass() {
      if (this.isImporting) return 'text-blue-600';
      if (this.progress === 100) return 'text-green-600';
      return 'text-gray-500';
    },
    allLogs() {
      return [...this.logs, ...this.errorLogs]
        .sort((a, b) => a.timestamp - b.timestamp);
    },
    isHeaderValid() {
      return this.headerErrors.length === 0;
    },
  },

  mounted() {
    // initWorker es async — disparamos sin await; el reintento en onFileSelected
    // cubre el caso donde el usuario interactúa antes de que el worker termine
    // de inicializar.
    this.initWorker();
  },

  updated() {
    this.syncPanelHeight();
  },

  methods: {
    syncPanelHeight() {
      this.$nextTick(() => {
        const panel = this.$refs.controlPanel;
        if (panel) {
          // Restar la altura del header de la tabla (~52px)
          const headerOffset = 52;
          const panelH = panel.offsetHeight - headerOffset;
          this.$el.style.setProperty('--panel-height', Math.max(panelH, 200) + 'px');
        }
      });
    },

    async initWorker() {
      try {
        this.excelWorker = await createExcelWorker();
      } catch (err) {
        console.error('Failed to instantiate ExcelWorker:', err);
        this.excelWorker = null;
        return;
      }

      this.excelWorker.onmessage = (event) => {
        const { type, data, columns, detectedHeaders, totalRows } = event.data;
        if (type === 'PREVIEW') {
          this.previewColumns = columns;
          this.previewRows = data;
          this.detectedHeaders   = detectedHeaders || [];
          this.totalRows = totalRows;
          this.isLoading = false;
          this.isReady = true;

          this.validateHeaders();
        } else if (type === 'READY') {
          this.isLoading = false;
          this.isReady = true;
        }
      };

      this.excelWorker.onerror = (err) => {
        console.error("Error en el Worker:", err);
        this.$toast.open({
          message: 'Error crítico al procesar el archivo.',
          type: 'is-danger',
          position: 'is-top-right',
        });
      };
    },

    validateHeaders() {
      const errors = [];
      const detected = this.detectedHeaders.map(h => String(h).trim());

      // Comparación case + space insensitive — tolera typos comunes como "id" vs "ID"
      const normalize = s => String(s || '').trim().toLowerCase();

      IMPORT_EXTRA_ATTRS_COLUMNS.forEach((col, idx) => {
        const expectedLabel = col.label;
        const detectedLabel = detected[idx];

        if (detectedLabel === undefined || detectedLabel === '') {
          errors.push(`Falta columna en la posición ${idx + 1}: "${expectedLabel}"`);
          return;
        }

        if (normalize(detectedLabel) !== normalize(expectedLabel)) {
          errors.push(`Columna ${idx + 1} debe ser "${expectedLabel}" — encontrada: "${detectedLabel}"`);
        }
      });

      this.headerErrors = errors;
    },

    addLog(message) {
      this.logs.push({
        type: 'info',
        time: new Date().toLocaleTimeString(),
        timestamp: Date.now(),
        message,
      });
      this.$nextTick(() => {
        if (this.$refs.logContainer) {
          this.$refs.logContainer.scrollTop = this.$refs.logContainer.scrollHeight;
        }
      });
    },

    /**
     * Genera y descarga un CSV con todos los logs (info + errores).
     * Los errores se desagregan: una fila por campo con error.
     */
    downloadReport() {
      if (this.allLogs.length === 0) return;

      const header = ['Tipo', 'Hora', 'Fila', 'Campo', 'Mensaje'];
      const rows = [header.join(',')];

      // allLogs ya viene ordenado cronológicamente
      this.allLogs.forEach(log => {
        if (log.type === 'error' && log.fieldErrors) {
          // Una fila por campo con error
          Object.entries(log.fieldErrors).forEach(([campo, msg]) => {
            rows.push([
              this.csvField('error'),
              this.csvField(log.time),
              this.csvField(log.row),
              this.csvField(campo),
              this.csvField(msg),
            ].join(','));
          });
        } else {
          // Info logs o errores sin estructura → una fila
          rows.push([
            this.csvField(log.type),
            this.csvField(log.time),
            this.csvField(''),
            this.csvField(''),
            this.csvField(log.message),
          ].join(','));
        }
      });

      // BOM UTF-8 para que Excel reconozca acentos correctamente
      const BOM = '\uFEFF';
      const csv = BOM + rows.join('\r\n');

      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;

      const layerCode = this.layer?.code || 'capa';
      const filename  = `importacion_pictogram_${layerCode}_extra_attributes_${dateStr}.csv`;

      // Crear blob y disparar descarga
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    /**
     * Escapa un valor para que sea seguro en una celda CSV.
     * - null/undefined → string vacío
     * - Si contiene coma, comilla o salto de línea: envuelve en comillas y dobla las internas
     */
    csvField(value) {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    },

    /**
     * Agrega al panel de errores el detalle por fila devuelto por el backend.
     * Cada error es { row, errors: { campo: mensaje, ... } }
     */
     appendRowErrors(errors) {
      if (!Array.isArray(errors) || errors.length === 0) return;

      const now       = new Date().toLocaleTimeString();
      const timestamp = Date.now();

      errors.forEach(err => {
        const detail = Object.entries(err.errors || {})
          .map(([campo, msg]) => `${campo}: ${msg}`)
          .join('; ');

        this.errorLogs.push({
          type: 'error',
          time: now,
          timestamp,
          message: `Fila ${err.row} — ${detail}`,
          // Estructura original — usada por el CSV para una fila por campo
          row: err.row,
          fieldErrors: err.errors || {},
        });
      });

      // Scroll al final
      this.$nextTick(() => {
        if (this.$refs.logContainer) {
          this.$refs.logContainer.scrollTop = this.$refs.logContainer.scrollHeight;
        }
      });
    },

    clearLogs() {
      this.logs = [];
      this.errorLogs = [];
    },

    resetImport() {
      this.form.excelFile = null;
      this.previewRows = [];
      this.previewColumns = [];
      this.totalRows = 0;
      this.processedRows = 0;
      this.createdRows = 0;
      this.updatedRows = 0;
      this.failedRows = 0;
      this.isImporting = false;
      this.isReady = false;
      this.isProcessing = false;
      this.clearLogs();

      this.detectedHeaders = [];
      this.headerErrors = [];
    },

    onFileSelected(file) {
      if (!file) return;

      this.isProcessing = true;
      this.isLoading = true;
      this.isReady = false;
      this.clearLogs();

      const reader = new FileReader();

      reader.onload = async (event) => {
        // Reintentar init si el worker quedó null tras mounted (async pendiente
        // o instanciación falló silenciosa).
        if (!this.excelWorker) {
          await this.initWorker();
        }
        if (!this.excelWorker) {
          this.isLoading = false;
          this.isProcessing = false;
          this.$toast.open({
            message: 'No se pudo iniciar el procesador del archivo. Revisá la consola.',
            type: 'is-danger',
            position: 'is-top-right',
          });
          return;
        }
        const data = event.target.result;
        this.excelWorker.postMessage({
          type: 'INIT',
          fileData: data,
          spec: IMPORT_EXTRA_ATTRS_COLUMNS,
          headerRows: IMPORT_EXTRA_ATTRS_HEADER_ROWS,
        });
      };

      reader.onerror = () => {
        this.$toast.open({
          message: 'Error al leer el archivo. Intente nuevamente.',
          type: 'is-danger',
          position: 'is-top-right',
        });
        this.isLoading = false;
      };

      reader.readAsArrayBuffer(file);
    },

    async startImport() {
      this.isImporting = true;
      this.processedRows = 0;
      this.createdRows  = 0;
      this.updatedRows  = 0;
      this.failedRows = 0;
      this.clearLogs();
      this.addLog("Iniciando importación masiva...");

      const startRow = IMPORT_EXTRA_ATTRS_HEADER_ROWS;
      const chunkSize = 100;

      try {
        for (let i = startRow; i < (this.totalRows + startRow); i += chunkSize) {
          const chunk  = await this.requestChunkFromWorker(i, chunkSize);
          const result = await this.uploadChunk(chunk, i - startRow);

          this.processedRows = Math.min(i + chunkSize - startRow, this.totalRows);
          this.createdRows  += result.created;
          this.updatedRows  += result.updated;
          this.failedRows   += result.failed;

          const chunkNum = Math.floor((i - startRow) / chunkSize) + 1;

          if (result.failed > 0) {
            this.addLog(`Chunk #${chunkNum}: ${result.created} asignados, ${result.updated} actualizados, ${result.failed} fallidos`);
            this.appendRowErrors(result.errors);
          } else {
            this.addLog(`Chunk #${chunkNum}: ${result.created} asignados, ${result.updated} actualizados`);
          }

          await this.$nextTick();
        }
      } catch (error) {
        // Error de configuración (schema_mismatch, unsupported_layer) o de red
        this.isImporting = false;

        const code = error.error_code || 'unknown';
        const msg  = error.error_msg || 'Error inesperado al enviar el chunk.';

        this.addLog(`Importación abortada (${code}): ${msg}`);
        this.errorLogs.push({
          type: 'error',
          time: new Date().toLocaleTimeString(),
          timestamp: Date.now(),
          message: `Configuración inválida — ${msg}`,
        });

        this.$toast.open({
          message: `Importación abortada: ${msg}`,
          type: 'is-danger',
          position: 'is-top-right',
          duration: 6000,
        });

        return;
      }

      this.isImporting = false;

      if (this.failedRows > 0) {
        this.addLog(`Importación finalizada: ${this.createdRows} asignados, ${this.updatedRows} actualizados, ${this.failedRows} fallidos.`);
        this.$toast.open({
          message: `Importación finalizada: ${this.createdRows} asignados, ${this.updatedRows} actualizados, ${this.failedRows} fallidos.`,
          type: 'is-warning',
          position: 'is-top-right',
          duration: 5000,
        });
      } else {
        this.addLog(`Importación completada: ${this.createdRows} asignados, ${this.updatedRows} actualizados.`);
        this.$toast.open({
          message: `Importación completada: ${this.createdRows} asignados, ${this.updatedRows} actualizados.`,
          type: 'is-success',
          position: 'is-top-right',
          duration: 5000,
        });
      }

      // Al terminar (import no abortada): persistir el Excel importado para poder
      // descargarlo luego. No bloquea ni revierte la importación ya aplicada.
      await this._persistImportedFile();
    },

    /**
     * Sube el Excel importado a S3 y lo vincula a la capa (crea o versiona el
     * documento). Best-effort: si falla, la importación de datos ya quedó hecha.
     */
    async _persistImportedFile() {
      if (!this.form.excelFile || !this.layer?.id) return;
      try {
        this.addLog('Guardando archivo importado…');
        const aws = await uploadFile(this.form.excelFile);
        if (!aws || !aws.key) {
          this.addLog('No se pudo subir el archivo importado.');
          return;
        }
        await storeExtraAttributesDocument(this.layer.id, {
          aws_response: JSON.stringify(aws),
          original_filename: this.form.excelFile.name,
        });
        this.addLog('Archivo importado guardado.');
      } catch (e) {
        this.addLog('No se pudo guardar el archivo importado (los datos sí se aplicaron).');
      }
    },

    requestChunkFromWorker(index, chunkSize) {
      return new Promise((resolve) => {
        const originalOnMessage = this.excelWorker.onmessage;

        this.excelWorker.onmessage = (e) => {
            if (e.data.type === 'CHUNK_READY') {
              this.excelWorker.onmessage = originalOnMessage;
              resolve(e.data.data);
            }
        };

        this.excelWorker.postMessage({
          type: 'GET_CHUNK',
          startIndex: index,
          chunkSize: chunkSize,
          columns: this.previewColumns
        });
      });
    },

    async uploadChunk(chunk, baseRow) {
      const response = await importExtraAttributes(this.layer.id, {
        schema: chunk.schema,
        rows: chunk.rows,
        baseRow: baseRow
      });

      if (response.success) {
        return {
          created: response.data.created,
          updated: response.data.updated,
          failed:  response.data.failed,
          errors:  response.data.errors || [],
        };
      }

      // Si el backend respondió pero con success=false (ej. schema mismatch),
      // contamos TODAS las filas del chunk como fallidas — refleja la realidad
      // mejor que el "1" hardcoded de antes.
      return {
        created: 0,
        updated: 0,
        failed:  chunk.rows.length,
        errors:  [],
      };
    },

    async downloadTemplate() {
      this.isCheckingTemplate = true;
      const layerCode = this.layer?.code || 'pilings';
      const filename  = `template_import_pictogram_${layerCode}_extra_attributes.xlsx`;
      const url       = this.assetVapor(filename);

      try {
        // Verificar existencia antes de descargar
        const response = await fetch(url, { method: 'HEAD' });

        if (!response.ok) {
          this.$toast.open({
            message: `No hay plantilla disponible para esta capa (${layerCode}).`,
            type: 'is-warning',
            position: 'is-top-right',
            duration: 4000,
          });
          return;
        }

        // OK → disparar descarga
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

      } catch (error) {
        // Falla de red, CORS u otra cosa imprevista
        this.$toast.open({
          message: 'No se pudo verificar la plantilla. Revisa tu conexión.',
          type: 'is-danger',
          position: 'is-top-right',
          duration: 4000,
        });
      } finally {
        this.isCheckingTemplate = false;   // ← se ejecuta SIEMPRE
      }
    },
  },

  beforeDestroy() {
    if (this.excelWorker) {
      this.excelWorker.terminate();
    }
  },
}
</script>

<style scoped>
/* --- Panel de control corporativo --- */
.panel-control {
  border: 1px solid #ADC2EA;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.panel-control__header {
  background: linear-gradient(135deg, #5577BB, #385DA5);
  padding: 10px 16px;
}

.panel-control__body {
  padding: 16px;
}

.panel-info-group {
  border: 1px solid #ADC2EA;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}

.panel-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid #e8effa;
}

.panel-info-row:last-child {
  border-bottom: none;
}

.panel-info-row:nth-child(odd) {
  background-color: #f8faff;
}

.panel-info-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7D99D4;
}

.panel-info-value {
  font-size: 12px;
  font-weight: 600;
  color: #385DA5;
  max-width: 160px;
  text-align: right;
}

/* --- Progress bar --- */
.progress-track {
  width: 100%;
  height: 10px;
  background: #e8effa;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid #ADC2EA;
}

.progress-bar {
  height: 100%;
  min-width: 0;
  border-radius: 999px;
  background: linear-gradient(90deg, #5577BB, #385DA5);
  transition: width 0.4s ease;
  position: relative;
}

.progress-bar--active {
  background: linear-gradient(90deg, #5577BB, #7D99D4, #5577BB);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}

.progress-bar--complete {
  background: linear-gradient(90deg, #22c55e, #16a34a);
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* --- Contadores --- */
.counter-box--success {
  border: 1.5px solid #4ade80;
  background-color: rgba(74, 222, 128, 0.1);
}

.counter-box--failed {
  border: 1.5px solid #d97706;
  background-color: rgba(217, 119, 6, 0.1);
}

/* --- Tabla corporativa --- */
.table-scroll {
  max-height: var(--panel-height, 300px);
  overflow-y: auto;
  overflow-x: auto;
}

.table-header {
  position: sticky;
  top: 0;
  z-index: 2;
}

.table-th {
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: left;
  white-space: nowrap;
  color: #fff;
  background-color: #5577BB;
  border: 1px solid #385DA5;
}

.table-td {
  padding: 6px 12px;
  white-space: nowrap;
  color: #374151;
  border: 1px solid #ADC2EA;
  font-size: 13px;
}

.table-row--even {
  background-color: #fff;
}

.table-row--odd {
  background-color: #f8faff;
}

.table-row:hover > .table-td {
  background-color: rgba(173, 194, 234, 0.174);
}

.table-scroll::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.table-scroll::-webkit-scrollbar-track {
  background: #eff8ff;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: #ADC2EA;
  border-radius: 3px;
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: #7D99D4;
}

/* --- Log corporativo --- */
.log-container {
  background: #fff;
  padding: 12px;
  font-family: 'Menlo', 'Consolas', 'Monaco', monospace;
  font-size: 11px;
  height: 160px;
  overflow-y: auto;
}

.log-entry {
  padding: 3px 8px;
  border-radius: 3px;
  margin-bottom: 2px;
  color: #385DA5;
}

.log-entry:nth-child(odd) {
  background-color: #f8faff;
}

.log-entry--error {
  color: #dc2626;
  font-weight: 600;
  background-color: #fef2f2 !important;
  border-left: 3px solid #dc2626;
}

.log-time {
  color: #7D99D4;
  font-weight: 400;
}

.log-container::-webkit-scrollbar {
  width: 5px;
}

.log-container::-webkit-scrollbar-track {
  background: #eff8ff;
}

.log-container::-webkit-scrollbar-thumb {
  background: #ADC2EA;
  border-radius: 3px;
}

.log-container::-webkit-scrollbar-thumb:hover {
  background: #7D99D4;
}

.header-errors-block {
  background-color: #fef2f2;
  border: 1px solid #fca5a5;
  border-left: 3px solid #dc2626;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.header-errors-title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}

.header-errors-list {
  list-style: disc;
  padding-left: 20px;
  color: #991b1b;
  font-size: 12px;
  line-height: 1.5;
}

.counter-box--info {
  border: 1.5px solid #3b82f6;
  background-color: rgba(59, 130, 246, 0.1);
}

.header-ok-block {
    background-color: #ecfdf5;
    border: 1px solid #6ee7b7;
    border-left: 3px solid #10b981;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 12px;
  }

  .header-ok-text {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #047857;
    font-size: 12px;
    font-weight: 600;
    margin: 0;
  }
</style>
